import { Modal, Table, TableBody, TableCell, TableHead, TableRow } from "@material-ui/core";
import { Column, ModalBody, Row } from "./customerSettings.styled";
import { Color } from "config/colors";
import { Text } from "components/Typography/text.styled";
import React, { useEffect, useState } from "react";
import { CompanyCustomer, CompanyCustomerProduct } from "model/Company";
import { FontFamily } from "config/font";
import { Api } from "services/api/api.service";
import { useTranslation } from "react-i18next";
import { DatePickerWithLabel } from "../../general/datePickerWithLabel/datePickerWithLabel.component";
import { MultiSelectOption, MultiSelectWithLabel } from "../../general/multiSelectWithLabel/multiSelectWithLabel.component";
import { MultiSelectContainer } from "../../general/multiSelectWithLabel/multiSelectWithLabel.styled";

type Props = {
  setShowCustomerInvoiceModal: React.Dispatch<React.SetStateAction<boolean>>;
  showCustomerInvoiceModal: boolean;
  customer?: CompanyCustomer;
  companyId?: number;
};

export const CustomerInvoiceModal: React.FC<Props> = ({
  setShowCustomerInvoiceModal,
  showCustomerInvoiceModal,
  customer,
  companyId
}) => {
  const { t } = useTranslation();

  const [customerProducts, setCustomerProducts] = useState<CompanyCustomerProduct[]>([]);
  const [selectedCustomerProducts, setSelectedCustomerProducts] = useState<any[]>([]);
  const [startDate, setStartDate] = useState<Date>();
  const [endDate, setEndDate] = useState<Date>();
  const [invoiceRows, setInvoiceRows] = useState<any[]>([])

  useEffect(() => {
    if (customer?.agreements) {
      let products = customer?.agreements[0].products;
      setCustomerProducts(products);
    }
  }, [customer]);

  const getInvoice = () => {
    setInvoiceRows([]);
    
    if (startDate && endDate && companyId) {
      let utcStart = Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
      let utcEnd = Date.UTC(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
      Api().company.getInvoiceInformationForPeriod(new Date(utcStart), new Date(utcEnd), selectedCustomerProducts, companyId)
          .then((res: any) => {
            let nonEmptyRows = res.data.filter((i: any) => i.totalWorkHours > 0);
            let sorted = nonEmptyRows.sort((a: any,b: any) => a.product.localeCompare(b.product));

            setInvoiceRows(sorted);
          })
          .catch(err => console.error(err));
    }
  }
  
  const onModalClose = () => {
    setShowCustomerInvoiceModal(false); 
    setInvoiceRows([]);
    setSelectedCustomerProducts([])
  }

  return <>
  <Modal title={customer?.name} open={showCustomerInvoiceModal}
  onClose={() => onModalClose()}>
    <ModalBody style={{width: '70%', top: '40%'}}>
      <Row>
        <Column>
          <Text style={{fontFamily: FontFamily.MontserratSemiBold, fontSize: '1.5rem'}}>{t('CompanyCustomers.CreateInvoice')}</Text>
        </Column>
      </Row>

      <Row style={{paddingLeft: '1rem'}}>
        <Column>
          <Text style={{fontFamily: FontFamily.MontserratSemiBold, fontSize: '1rem'}}>{t('CompanyCustomers.CustomerInformation')}</Text>
        </Column>
      </Row>
      <Row style={{paddingLeft: '1rem'}}>
        <Column style={{fontFamily: FontFamily.MontserratRegular, fontSize: '0.8rem'}}>{t('CompanyCustomers.Name')}: {customer?.name}</Column>
      </Row>
      <Row style={{paddingLeft: '1rem'}}>
        <Column style={{fontFamily: FontFamily.MontserratRegular, fontSize: '0.8rem'}}>{t('CompanyCustomers.OrganizationNumber')}: {customer?.organizationNumber}</Column>
      </Row>
      <Row style={{paddingLeft: '1rem'}}>
        <Column style={{fontFamily: FontFamily.MontserratRegular, fontSize: '0.8rem'}}>{t('CompanyCustomers.Contact')}: {customer?.contact}</Column>
      </Row>

      <Row style={{paddingLeft: '1rem'}}>
        <Column>
          <Text style={{fontFamily: FontFamily.MontserratSemiBold, fontSize: '1rem'}}>{t('CompanyCustomers.ChooseInformationForInvoice')}</Text>
        </Column>
      </Row>

      <>
      <Row style={{paddingLeft: '1rem', display: 'flex', justifyItems: 'flex-end'}}>
        <Column>
          <DatePickerWithLabel
                  label={t("CreateShift.Date")}
                  required
                  rangeOnly
                  onRangeChange={(dates: Date[]) => {
                    setStartDate(dates[0]);
                    setEndDate(dates[dates.length-1]);
                  }}
                  placeholder={t("OrganizationalStructure.ChooseDate")}
                  dateFormat="YYYY-MM-DD"
                  allowRangePicker
                />
        </Column>
        <Column style={{width: 500}}>
          <MultiSelectContainer maxWidth={600} style={{marginTop: '-1rem'}}>
            <MultiSelectWithLabel
              label={t('CompanyCustomers.Products')}
              required
              options={customerProducts.map(p => ({label: p.title, value: p.id} as MultiSelectOption))}
              values={selectedCustomerProducts}
              onChange={(e) =>  {
                setSelectedCustomerProducts(e);
              }}
              placeholder={t('CompanyCustomers.NotChosen')}
              maxDisplayTags={3}
              showSelectAll
            />
          </MultiSelectContainer>
        </Column>
        <Column>
          <Text style={{cursor: 'pointer', marginTop: '-0.5rem', border: `1px solid ${Color.BurntSienna}`, padding: 10, borderRadius: 10}} 
          onClick={() => getInvoice()} color={Color.BurntSienna}>{t('CompanyCustomers.GetInvoiceInformation')}</Text>
        </Column>
        
      </Row>
      </>

      { invoiceRows.length > 0 && 
      <Table style={{marginBottom: 20, paddingLeft: '1rem'}} size="small" aria-label="purchases">
        <TableHead>
          <TableRow>
            <TableCell style={{ fontFamily: FontFamily.MontserratRegular }}>{t('CompanyCustomers.Name')}</TableCell>
            <TableCell style={{ fontFamily: FontFamily.MontserratRegular }}>{t('CompanyCustomers.Product')}</TableCell>
            <TableCell style={{ fontFamily: FontFamily.MontserratRegular }}>{t('CompanyCustomers.Price')}</TableCell>
            <TableCell style={{ fontFamily: FontFamily.MontserratRegular }}>{t('CompanyCustomers.TotalHours')}</TableCell>
            <TableCell style={{ fontFamily: FontFamily.MontserratRegular }}>{t('CompanyCustomers.TotalPrice')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody style={{paddingLeft: '1rem'}}>
          { invoiceRows.map((invoiceRow: any) => (
            <TableRow key={invoiceRow.gigId}>
              <TableCell style={{ fontFamily: FontFamily.MontserratRegular }} component="th" scope="row">
                {invoiceRow.workerName}
              </TableCell>
              <TableCell style={{ fontFamily: FontFamily.MontserratRegular }} component="th" scope="row">
                {invoiceRow.product}
              </TableCell>
              <TableCell style={{ fontFamily: FontFamily.MontserratRegular }}>{invoiceRow.hourlyRate}</TableCell>
              <TableCell style={{ fontFamily: FontFamily.MontserratRegular }}>{invoiceRow.totalWorkHours}</TableCell>
              <TableCell style={{ fontFamily: FontFamily.MontserratRegular }}>{invoiceRow.totalPrice}</TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell style={{ fontFamily: FontFamily.MontserratRegular, fontWeight: 600 }}>{t('CompanyCustomers.Total')}</TableCell>
            <TableCell style={{ fontFamily: FontFamily.MontserratRegular, fontWeight: 600 }}>{invoiceRows.reduce((p,c) => p += c.totalWorkHours, 0)}</TableCell>
            <TableCell style={{ fontFamily: FontFamily.MontserratRegular, fontWeight: 600 }}>{invoiceRows.reduce((p,c) => p += c.totalPrice, 0)} SEK {t('CompanyCustomers.ExcludingVAT')}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
      }
    </ModalBody>
  </Modal>
  </>
}